import { Box } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { CheckCircleRounded, CancelRounded } from "@mui/icons-material";

export type StatusPillLevel = "success" | "warning" | "error" | "neutral";

export type StatusPillProps = {
  label: string;
  level: StatusPillLevel;
  icon?: ReactNode;
  size?: "small" | "medium";
};

const StatusPill = ({ label, level, icon, size = "medium" }: StatusPillProps) => {
  const theme = useTheme();

  const paletteByLevel: Record<StatusPillLevel, { bg: string; text: string }> = {
    success: {
      bg: alpha(theme.palette.success.main, 0.15),
      text: theme.palette.success.main,
    },
    warning: {
      bg: alpha(theme.palette.warning.main, 0.15),
      text: theme.palette.warning.main,
    },
    error: {
      bg: alpha(theme.palette.error.main, 0.15),
      text: theme.palette.error.main,
    },
    neutral: {
      bg: alpha(theme.palette.text.secondary, 0.12),
      text: theme.palette.text.secondary,
    },
  };

  const palette = paletteByLevel[level];
  const defaultIcon =
    level === "success" ? (
      <CheckCircleRounded sx={{ fontSize: size === "small" ? 14 : 16 }} />
    ) : level === "error" ? (
      <CancelRounded sx={{ fontSize: size === "small" ? 14 : 16 }} />
    ) : null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: size === "small" ? 1 : 1.5,
        py: size === "small" ? 0.25 : 0.5,
        borderRadius: "999px",
        backgroundColor: palette.bg,
        color: palette.text,
        fontSize: size === "small" ? "0.6875rem" : "0.75rem",
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {icon !== undefined ? icon : defaultIcon}
      {label}
    </Box>
  );
};

export default StatusPill;
